import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Socket } from 'phoenix';
import log from '../lib/log.js';
import Wake from './wake.js';
import { doGetAppConfig } from '../actions/application-actions.js';
import {
  successUpdateLocation,
  doGetLocation,
} from '../actions/locations-actions.js';
import {
  successUpdateEmployee,
  doGetEmployee,
} from '../actions/employee-actions.js';
import { doSetAppVersion } from '../actions/application-actions.js';
import { doGetCleaning } from '../actions/cleaning-actions.js';
import {
  doGetRequisition,
  successCreateProductRequisition,
  successUpdateProductRequisition,
  successDeleteProductRequisition,
  submitUpdateRequisitionShippingPrice,
} from '../actions/requisition-actions.js';
import { doGetCart, doGetCarts, doAndUnsetResponse } from '../ducks/carts.js';
import { doGetCheckin } from '../actions/checkin-actions.js';
import {
  successGetShipment,
  successDeleteShipment,
  doAndUnsetResponse as doAndUnsetShipmentResponse,
} from '../ducks/shipments.js';
import { doGetCustomerBudgetCode } from '../actions/customer-budget-codes-actions.js';
import {
  successGetLocationListProduct,
  successDeleteLocationListProduct,
} from '../actions/location-list-product-actions.js';
import { doGetLocationList } from '../actions/location-list-actions.js';
import { doGetScheduledRequisition } from '../actions/scheduled-requisition-actions.js';
import {
  successCreateReview,
  successUpdateReview,
} from '../actions/review-actions.js';
import {
  requisitionSubmitted,
  requisitionApprovalRequested,
  requisitionProcessing,
  shipmentReceived,
} from '../actions/confirmations-actions.js';
import {
  successCreateLocationCatalogProduct,
  successDeleteLocationCatalogProduct,
} from '../actions/location-catalog-product-actions.js';
import { doGetCredit, successDeleteCredit } from '../actions/credit-actions.js';
import hmacFetch from '../lib/hmac-fetch.js';
import { ENV_STRING, GOSSIP_URL } from '../strings.js';

const logger = log('[gossip]');

export class Gossip extends React.PureComponent {
  socket = null;
  channels = [];
  retryTimeout = null;

  componentDidMount() {
    if (!this.props.ready) return;
    this.connect();
  }

  componentDidUpdate(prevProps) {
    if (!this.props.ready) {
      this.disconnect();
      return;
    }
    if (
      !prevProps.ready ||
      prevProps.locationId !== this.props.locationId ||
      prevProps.actor !== this.props.actor
    ) {
      this.disconnect();
      this.connect();
    }
  }

  componentWillUnmount() {
    this.disconnect();
  }

  getChannelNames() {
    const { actor, locationId } = this.props;
    const actorChannel = `${actor.type.toLowerCase()}-${actor.id}-${ENV_STRING}`;
    const locationChannel = `location-${locationId}-${ENV_STRING}`;
    return [
      'common_dash',
      locationChannel,
      actorChannel,
      `${locationChannel}:${actorChannel}`,
    ];
  }

  connect = () => {
    hmacFetch(`${GOSSIP_URL}/token`)
      .then((resp) => {
        if (resp.status !== 200) throw new Error('Token fetch failed');
        return resp.json();
      })
      .then((body) => {
        if (!body.token) {
          if (this.props.ready) {
            this.retryTimeout = setTimeout(this.connect, 1000);
          }
          return;
        }
        this.startSocket(body.token);
      })
      .catch(() => {
        if (this.props.ready) {
          this.retryTimeout = setTimeout(this.connect, 1000);
        }
      });
  };

  startSocket(token) {
    const wsUrl = GOSSIP_URL.replace(/^http/, 'ws') + '/socket';
    this.socket = new Socket(wsUrl, { params: { token } });
    this.socket.onError(() => logger.error('socket error'));
    this.socket.onClose(() => logger.info('socket closed'));
    this.socket.connect();

    this.getChannelNames().forEach((name) => {
      const channel = this.socket.channel(`gossip:${name}`, {});
      channel
        .join()
        .receive('ok', () => logger.info('joined:', name))
        .receive('error', (resp) => logger.error('join failed:', name, resp));
      channel.on('new_msg', (payload) => this.handleMessageJSON(payload));
      this.channels.push(channel);
    });
  }

  disconnect() {
    this.retryTimeout && window.clearTimeout(this.retryTimeout);
    this.channels.forEach((ch) => ch.leave());
    this.channels = [];
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  handleEventActions = (message) => {
    if (message.data && message.data.app_version) {
      return this.props.actions.doSetAppVersion(message.data.app_version);
    } else if (message.data && message.data.app_config) {
      return this.props.actions.doGetAppConfig(this.props.cart.location_id);
    }
    return logger.warn('unhandled:', message);
  };

  handleMessageJSON = (message) => {
    logger.info(message);
    switch (message.type) {
      case 'event':
        return this.handleEventActions(message);
      case 'checkin.created':
      case 'checkin.updated':
        return this.handleCheckin(message);
      case 'cleaning.created':
      case 'cleaning.updated':
        return this.handleCleaning(message);
      case 'employee.created':
      case 'employee.updated':
        return this.handleEmployee(message);
      case 'customer_budget_code.created':
      case 'customer_budget_code.updated':
        return this.handleCustomerBudgetCode(message);
      case 'location.updated':
        return this.handleLocation(message);
      case 'location_list.updated':
        return this.handleLocationList(message);
      case 'location_catalog_product.created':
        return this.handleLocationCatalogCreated(message);
      case 'location_catalog_product.deleted':
        return this.handleLocationCatalogDeleted(message);
      case 'location_list_product.created':
      case 'location_list_product.updated':
      case 'location_list_product.deleted':
        return this.handleLocationListProduct(message);
      case 'product_requisition.created':
      case 'product_requisition.updated':
      case 'product_requisition.deleted':
        return this.handleProductRequisition(message);
      case 'requisition.created':
      case 'requisition.updated':
        return this.handleRequisition(message);
      case 'requisition.submitted':
        return this.handleRequisitionSubmitted(message);
      case 'requisition.approval_requested':
        return this.handleRequisitionApprovalRequested(message);
      case 'review.created':
      case 'review.updated':
        return this.handleReview(message);
      case 'scheduled_requisition.created':
      case 'scheduled_requisition.updated':
        return this.handleScheduledRequisition(message);
      case 'shipment.created':
      case 'shipment.updated':
      case 'shipment.deleted':
        return this.handleShipment(message);
      case 'credit.updated':
      case 'credit.created':
      case 'credit.deleted':
        return this.handleCredit(message);
      case 'shipment.received':
        return this.handleShipmentReceived(message);
      default:
        return logger.warn('unhandled:', message);
    }
  };

  handleCredit = (message) => {
    if (message.type.match(/deleted/)) {
      this.props.actions.successDeleteCredit(message.data);
    } else {
      this.props.actions.doGetCredit(message.data);
    }
    if (
      message.data.creditable_type === 'PromoCode' &&
      this.props.cart.id === message.data.requisition_id
    ) {
      this.props.actions.doGetCart({
        id: message.data.requisition_id,
        location_id: message.data.location_id,
      });
    }
  };

  handleCheckin = (message) => {
    return this.props.actions.doGetCheckin(message.data);
  };

  handleCleaning = (message) => {
    return this.props.actions.doGetCleaning(message.data);
  };

  handleCustomerBudgetCode = (message) => {
    if (this.messageByActor(message)) {
      return;
    }
    return this.props.actions.doGetCustomerBudgetCode(message.data);
  };

  handleEmployee = (message) => {
    if (this.messageByActor(message)) return;

    return this.props.actions.successUpdateEmployee(message.data);
  };

  handleLocation = (message) => {
    if (!message.data.active) return;
    if (this.messageByActor(message)) return;

    this.props.actions.successUpdateLocation(message.data);

    let changes = Object.keys(message.changes || {});

    if (
      ['free_shipping', 'shipping_fee', 'shipping_minimum'].some((key) =>
        changes.includes(key)
      )
    ) {
      this.props.actions.doGetAppConfig(this.props.cart.location_id);
      this.props.actions.submitUpdateRequisitionShippingPrice();
    }

    return;
  };

  handleLocationList = (message) => {
    return this.props.actions.doGetLocationList(message.data);
  };

  handleLocationCatalogCreated = (message) => {
    return this.props.actions.successCreateLocationCatalogProduct(message.data);
  };

  handleLocationCatalogDeleted = (message) => {
    return this.props.actions.successDeleteLocationCatalogProduct(message.data);
  };

  handleLocationListProduct = (message) => {
    if (this.messageByActor(message)) return;

    if (message.type.match(/deleted/)) {
      return this.props.actions.successDeleteLocationListProduct(message.data);
    }
    return this.props.actions.successGetLocationListProduct(message.data);
  };

  handleShipment = (message) => {
    if (this.messageByActor(message)) {
      return;
    }
    if (message.type.match(/deleted/)) {
      return this.props.actions.successDeleteShipment(message.data);
    }
    return this.props.actions.successShipment({
      item: message.data,
    });
  };

  handleProductRequisition = (message) => {
    if (this.messageByActor(message)) return;

    if (message.type === 'product_requisition.created') {
      this.props.actions.successCreatePR(message.data);
    }
    if (message.type === 'product_requisition.updated') {
      this.props.actions.doGetCart({
        id: message.data.requisition_id,
        location_id: this.props.cart.location_id,
      });
    }
    if (message.type === 'product_requisition.deleted') {
      this.props.actions.successDeletePR(message.data);
    }
  };

  handleRequisition = (message) => {
    if (this.messageByActor(message) && message.type.match(/created/)) {
      return;
    }
    if (message.data.status.match(/claimed|deferred/)) {
      this.props.actions.requisitionProcessing(message);
    }

    if (message.data.status.match(/draft/)) {
      this.props.actions.doGetCart(message.data);
    } else {
      this.props.actions.doGetRequisition(message.data);
    }
    return;
  };

  handleRequisitionSubmitted = (message) => {
    if (!this.messageByActor(message)) {
      this.props.actions.requisitionSubmitted(message);
    }
    this.props.actions.doGetCarts(message.data);
    this.props.actions.doGetCart(this.props.cart);
  };

  handleReview = (message) => {
    if (this.messageByActor(message)) {
      return;
    }
    if (message.type.match(/created/)) {
      return this.props.actions.successCreateReview(message.data);
    }
    if (message.type.match(/updated/)) {
      return this.props.actions.successUpdateReview(message.data);
    }
  };

  handleRequisitionApprovalRequested = (message) => {
    if (this.messageByActor(message)) {
      return;
    }
    this.props.actions.requisitionApprovalRequested(message);
    this.props.actions.doGetCart(message.data);
  };

  handleShipmentReceived = (message) => {
    if (this.messageByActor(message)) {
      return;
    }
    return this.props.actions.shipmentReceived(message);
  };

  handleScheduledRequisition = (message) => {
    if (
      this.messageByActor(message) &&
      message.data.id === this.props.openScheduledRequisitionId
    ) {
      return;
    }
    return this.props.actions.doGetScheduledRequisition(message.data);
  };

  messageByActor = (message) => {
    return (
      message.actor.id === this.props.actor.id &&
      message.actor.type.toLowerCase() === this.props.actor.type.toLowerCase()
    );
  };

  handleWake = () => {
    this.disconnect();
    this.connect();
  };

  render() {
    return <Wake onWake={this.handleWake} interval={1000 * 60} />;
  }
}

Gossip.propTypes = {
  ready: PropTypes.bool.isRequired,
  locationId: PropTypes.number.isRequired,
  actor: PropTypes.shape({
    id: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  cart: PropTypes.shape({
    id: PropTypes.number.isRequired,
    location_id: PropTypes.number,
  }).isRequired,
  actions: PropTypes.shape({
    requisitionSubmitted: PropTypes.func.isRequired,
    requisitionApprovalRequested: PropTypes.func.isRequired,
    requisitionProcessing: PropTypes.func.isRequired,
    shipmentReceived: PropTypes.func.isRequired,
    successDeleteShipment: PropTypes.func.isRequired,
    successUpdateReview: PropTypes.func.isRequired,
    successCreateReview: PropTypes.func.isRequired,
    doGetEmployee: PropTypes.func.isRequired,
    doGetCleaning: PropTypes.func.isRequired,
    doSetAppVersion: PropTypes.func.isRequired,
    doGetRequisition: PropTypes.func.isRequired,
    doGetLocation: PropTypes.func.isRequired,
    doGetLocationList: PropTypes.func.isRequired,
    doGetCheckin: PropTypes.func.isRequired,
    doGetAppConfig: PropTypes.func.isRequired,
    doGetCustomerBudgetCode: PropTypes.func.isRequired,
    doGetScheduledRequisition: PropTypes.func.isRequired,
    doGetCredit: PropTypes.func.isRequired,
    successDeleteCredit: PropTypes.func.isRequired,
    doGetCart: PropTypes.func.isRequired,
    successCreatePR: PropTypes.func.isRequired,
    successUpdatePR: PropTypes.func.isRequired,
    successDeletePR: PropTypes.func.isRequired,
  }).isRequired,
};

function mapStateToProps(state) {
  return {
    actor: state.auth.actor,
    locationId: state.locations.open.id,
    ready: !!(state.locations.open.id && state.auth.hmac),
    openScheduledRequisitionId: state.scheduledRequisitions.open.id,
    cart: state.carts.open,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        successUpdateEmployee,
        successUpdateLocation,
        doGetCleaning,
        doSetAppVersion,
        doGetRequisition,
        successCreateLocationCatalogProduct,
        successDeleteLocationCatalogProduct,
        doGetLocation,
        successGetLocationListProduct,
        doGetLocationList,
        successDeleteLocationListProduct,
        doGetCheckin,
        doGetEmployee,
        doGetAppConfig,
        doGetCustomerBudgetCode,
        doGetScheduledRequisition,
        requisitionSubmitted,
        requisitionApprovalRequested,
        requisitionProcessing,
        submitUpdateRequisitionShippingPrice,
        shipmentReceived,
        successDeleteShipment,
        successCreateReview,
        successUpdateReview,
        doGetCredit,
        successDeleteCredit,
        doGetCart,
        doGetCarts,
        successCreatePR: doAndUnsetResponse(successCreateProductRequisition),
        successUpdatePR: doAndUnsetResponse(successUpdateProductRequisition),
        successDeletePR: doAndUnsetResponse(successDeleteProductRequisition),
        successShipment: doAndUnsetShipmentResponse(successGetShipment),
      },
      dispatch
    ),
  };
}

function areStatesEqual(prev, next) {
  return (
    prev.locations.open.id === next.locations.open.id &&
    prev.auth.actor === next.auth.actor &&
    prev.auth.hmac === next.auth.hmac &&
    prev.scheduledRequisitions.open.id === next.scheduledRequisitions.open.id &&
    prev.carts.open.id === next.carts.open.id
  );
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  areStatesEqual,
})(Gossip);
